import { CircularProgress, Grid, makeStyles } from '@material-ui/core'
import Actions from 'actions'
import { InfoIcon } from 'components/common/icons'
import { DROPZONE_ACCEPTABLE_FILE_TYPES, FILE_UPLOAD_MAX_SIZE } from 'consts/appConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePatientTags from 'hooks/usePatientTags'
import usePlanLimits from 'hooks/usePlanLimits'
import useRolePermissions from 'hooks/useRolePermissions'
import useSortedMessages from 'hooks/useSortedMessages'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { trackEvent } from 'utils/analyticsUtils'
import { isUserOfAnyAdminRole } from 'utils/authUtils'
import { appendQueryParams } from 'utils/generalUtils'
import { isMobile } from 'utils/mobileUtils'
import { v4 as uuidv4 } from 'uuid'
import UploadDropzone from '../../common/UploadDropzone/UploadDropzone'
import PatientBriefQuickActionsButton from '../PatientBrief/QuickActionsFloatingPopup/PatientBriefQuickActionsButton'
import PatientBriefQuickActionsButtonMobile from '../PatientBrief/QuickActionsFloatingPopupMobile/PatientBriefQuickActionsButton.mobile'
import useLoadPatientFromUrlParams from '../useLoadPatientFromUrlParams'
import NewMessage from './NewMessage'
import PatientChatContainer from './PatientChatContainer'
import PatientNotInvitedState from './PatientNotInvitedState'
import { getChatDataState, shouldShowMobileChatRouteLoader } from './patientsChatLoadingUtils'

const useStyles = makeStyles({
  addedOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '50%',
    height: '100%',
    zIndex: 999
  },
  disabledChatContainer: {
    opacity: 0.5
  },
  sendInvitation: {
    marginTop: 10,
    fontSize: 14,
    fontWeight: 500
  },
  chatContainer: {
    flex: 1,
    position: 'relative',
    height: ({ isMobile, sendChatMessages }) => (isMobile && sendChatMessages ? 'calc(100% - 35px)' : '100%'),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    background: 'white',
    borderLeft: ({ isMobile, hideLeftBorder }) =>
      !isMobile && !hideLeftBorder ? '1px solid var(--border-color-7)' : ''
  },
  invitedPatientWarning: {
    background: '#FFBD39',
    padding: ({ isMobile }) => (isMobile ? 15 : 25),
    zIndex: 10,
    width: '100%',
    display: 'flex',
    '& svg': {
      width: 25,
      height: 25
    }
  },
  iconContainer: {
    width: 25,
    marginRight: 10
  },
  overlayLoader: {
    flex: 1,
    height: '100%'
  },
  messagesLoader: {
    padding: '12px'
  }
})

const PatientsChat = ({ hideLeftBorder = false }) => {
  const dispatch = useDispatch()
  const { permissions } = useRolePermissions()
  const isMobileView = isMobile()
  const classes = useStyles({ isMobile: isMobileView, sendChatMessages: permissions.sendChatMessages, hideLeftBorder })
  const { t } = useTranslation()
  const { patientId } = useParams()
  const { isChurnedPlan, isHI: isHiPractice } = usePlanLimits()
  const { copilotControl: copilotFF } = useFeatureFlags()

  const { patient, isLoadingPatient } = useSelector(state => state.patientsReducer)
  const { needsAttentionTagDetails } = usePatientTags({ patient })

  const treatment = useSelector(state => state.treatmentReducer.treatment)
  const messages = useSortedMessages()
  const doctor = useSelector(state => state.profileReducer.doctor)
  const { accountOwner, details: practiceDetails } = useSelector(state => state.practiceReducer)
  const isLoadingMessages = useSelector(state => state.chatReducer.messages.isLoading)
  const activeRoomId = useSelector(state => state.chatReducer.activeRoomId)
  const selectedRoomId = useSelector(
    state =>
      state.chatReducer.rooms.find(
        room => room.id === patientId || room.patientId === patientId || room.patient?.id === patientId
      )?.roomId
  )
  const { isModeOn: isBroadcastMode, localMessages: broadcastMessages } = useSelector(
    state => state.chatReducer.broadcast
  )

  const [filesToSend, setFilesToSend] = useState([])
  const [showDropzone, setShowDropzone] = useState(false)
  // The room this mount requested messages for. Only the e2e `data-state` reads it.
  const [requestedRoomId, setRequestedRoomId] = useState()

  const isAdmin = useMemo(() => isUserOfAnyAdminRole(), [])
  const roomId = useMemo(() => patient.user?.rooms?.items?.[0]?.roomId || selectedRoomId, [patient, selectedRoomId])

  const isActive = useMemo(
    () =>
      !isChurnedPlan &&
      (isBroadcastMode || (patient.user?.isActive && !treatment?.currentStatus?.statusKey?.includes('transferred'))),
    [patient, treatment, isBroadcastMode, isChurnedPlan]
  )

  const isRecordsOnly = useMemo(() => patient?.auth_status === 'added', [patient?.auth_status])
  const messageMembers = useMemo(
    () =>
      [
        patient?.username,
        doctor?.username !== patient?.doctor?.username ? patient?.doctor?.username : doctor?.username
      ].filter(member => !!member),
    [doctor, patient]
  )

  const showPatientInvitedBanner = useMemo(
    () => patient.auth_status === 'invited' && !isBroadcastMode && !showDropzone,
    [isBroadcastMode, patient.auth_status, showDropzone]
  )

  const patientFFs = useMemo(() => JSON.parse(patient?.user?.featureFlags?.flags || '{}'), [patient])
  const showMobileChatRouteLoader = useMemo(
    () =>
      shouldShowMobileChatRouteLoader({
        isMobileView,
        isBroadcastMode,
        patientId,
        patient,
        isLoadingPatient,
        roomId,
        activeRoomId,
        isLoadingMessages,
        messageCount: messages.items?.length
      }),
    [
      isMobileView,
      isBroadcastMode,
      patientId,
      patient,
      isLoadingPatient,
      roomId,
      activeRoomId,
      isLoadingMessages,
      messages.items?.length
    ]
  )

  useLoadPatientFromUrlParams({
    disabled: isBroadcastMode || isMobileView,
    onPatientRequestDispatched: () => {
      setShowDropzone(false)
    }
  })

  useEffect(() => {
    if (roomId && !isBroadcastMode) {
      roomId && dispatch(Actions.requestPatientMessages(roomId))
      setRequestedRoomId(roomId)
    }
  }, [dispatch, isBroadcastMode, roomId])

  useEffect(() => {
    if (isAdmin && patient?.patientDoctorId) {
      dispatch(Actions.requestPracticeMembers({ doctorId: patient?.patientDoctorId }))
    }
  }, [dispatch, isAdmin, patient?.patientDoctorId])

  const handleMessageSend = useCallback(
    ({ content, templateId }, options) => {
      if (isBroadcastMode) {
        dispatch(
          Actions.createBroadcastMessage({
            type: 'text',
            content,
            templateId
          })
        )
      } else {
        dispatch(
          Actions.sendChatMessage({
            members: messageMembers,
            roomId,
            type: 'text',
            content,
            patientUsername: patient.username,
            readBy: doctor.username,
            templateId,
            scheduleTime: options.scheduleTime,
            patientDoctorUsername: patient?.doctor?.username
          })
        )
      }
    },
    [dispatch, doctor, messageMembers, roomId, patient, isBroadcastMode]
  )

  const handleFileSend = useCallback(
    (files, options) => {
      trackEvent('Chat - Files sent', {
        fileType: files.map(file => file.messageType)
      })
      files.forEach(({ data, file, extension, fileTitle, id, messageType, type }) => {
        if (isBroadcastMode) {
          dispatch(
            Actions.createBroadcastFileMessage({
              id,
              type: messageType,
              content: file,
              cache: data,
              contentType: file.type,
              extension,
              metadata: JSON.stringify({
                fileTitle
              })
            })
          )
        } else {
          dispatch(
            Actions.sendChatMessage({
              id,
              members: messageMembers,
              roomId,
              type: messageType,
              content: file,
              cache: data,
              extension,
              patientUsername: patient.username,
              readBy: doctor.username,
              scheduleTime: options.scheduleTime,
              patientDoctorUsername: patient?.doctor?.username,
              metadata: JSON.stringify({
                fileTitle
              })
            })
          )
        }
      })
    },
    [dispatch, doctor, messageMembers, roomId, patient, isBroadcastMode]
  )

  const handlePromotionSend = useCallback(
    ({ imageUrl, id: promotionId, baseUrl, externalId }, options, type = 'promotion') => {
      if (isBroadcastMode) {
        dispatch(
          Actions.createBroadcastMessage({
            type: 'promotion',
            content: imageUrl,
            href: baseUrl,
            promotionId,
            entityRelatedId: promotionId
          })
        )
      } else {
        const messageId = uuidv4()
        const urlIncludingParams = appendQueryParams(baseUrl, {
          doctorId: accountOwner.rcToken,
          patientId: patient.publicId,
          doctorName: doctor.name,
          externalId,
          messageId
        })

        dispatch(
          Actions.sendChatMessage({
            id: messageId,
            type: 'promotion',
            members: messageMembers,
            roomId,
            patientUsername: patient.username,
            baseUrl: urlIncludingParams,
            promotionId,
            readBy: doctor.username,
            scheduleTime: options.scheduleTime,
            patientDoctorUsername: patient?.doctor?.username,
            content: imageUrl,
            entityRelatedId: promotionId
          })
        )
      }
    },
    [dispatch, doctor, messageMembers, patient, roomId, accountOwner, isBroadcastMode]
  )

  const loadMoreMessages = useCallback(() => {
    messages.nextToken &&
      !isLoadingMessages &&
      dispatch(Actions.requestPatientMessages(roomId, messages.nextToken, 'push'))
  }, [dispatch, messages, roomId, isLoadingMessages])

  const handleDropFile = useCallback(files => {
    if (files.length) {
      setShowDropzone(false)
      setFilesToSend(files)
    }
  }, [])

  const chatDataState = getChatDataState({
    showMobileChatRouteLoader,
    isBroadcastMode,
    patientId,
    patient,
    isLoadingPatient,
    roomId,
    requestedRoomId,
    isLoadingMessages,
    isRecordsOnly,
    isActive
  })

  return (
    <>
      {!isMobileView && isRecordsOnly && <PatientNotInvitedState />}
      <div
        id="patient-room-chat"
        data-state={chatDataState}
        className={`${classes.chatContainer} ${!isMobileView && isRecordsOnly && classes.disabledChatContainer}`}
      >
        {showMobileChatRouteLoader ? (
          <Grid container justifyContent="center" alignItems="center" className={classes.overlayLoader}>
            <CircularProgress size={24} />
          </Grid>
        ) : (
          <>
            {isHiPractice &&
              (!isMobileView ? (
                !copilotFF ? (
                  <PatientBriefQuickActionsButton
                    needsAttentionTagDetails={needsAttentionTagDetails}
                    patientId={patient?.id || patientId}
                    practiceName={practiceDetails?.practiceName}
                  />
                ) : (
                  <></>
                )
              ) : copilotFF ? (
                // The Copilot tab on mobile (see AppTabs.mobile.jsx) is the host for the
                // Needs Attention flow now. Skip the floating bubble to avoid it overlaying
                // the chat thread on every load.
                <></>
              ) : (
                <PatientBriefQuickActionsButtonMobile
                  needsAttentionTagDetails={needsAttentionTagDetails}
                  patientId={patient?.id || patientId}
                  practiceName={practiceDetails?.practiceName}
                />
              ))}
            {showPatientInvitedBanner && (
              <div id="patient-room-chat-invited-banner" className={classes.invitedPatientWarning}>
                <div className={classes.iconContainer}>
                  <InfoIcon />
                </div>
                {t('pages.patients.selectedPatient.chat.invitedMessage')}
              </div>
            )}
            {isLoadingMessages && (
              <Grid
                id="patient-room-chat-messages-loader"
                container
                justifyContent="center"
                alignItems="center"
                className={classes.messagesLoader}
              >
                <CircularProgress size={12} />
              </Grid>
            )}
            {!isRecordsOnly ? (
              <PatientChatContainer
                messages={isBroadcastMode ? broadcastMessages : messages.items}
                loadMoreMessages={loadMoreMessages}
                withMessageActions={!!patientFFs.messageModificationsAvailable}
              />
            ) : (
              isMobileView && <PatientNotInvitedState />
            )}
            {showDropzone && (
              <UploadDropzone
                id="patient-room-chat-dropzone"
                onUploadFile={handleDropFile}
                setShowDropzone={setShowDropzone}
                acceptedFormats={DROPZONE_ACCEPTABLE_FILE_TYPES}
                maxFileSize={FILE_UPLOAD_MAX_SIZE}
                showSavedFiles
                isBroadcastMode={isBroadcastMode}
              />
            )}
            <NewMessage
              containerClassName={isMobileView && isRecordsOnly ? classes.disabledChatContainer : ''}
              isActive={isActive}
              isAdmin={!permissions.sendChatMessages}
              handleMessageSend={handleMessageSend}
              handleFileSend={handleFileSend}
              handlePromotionSend={handlePromotionSend}
              disablePromotion={patient?.user?.version === '1.27.5'}
              setShowDropzone={setShowDropzone}
              filesToSend={filesToSend}
              setFilesToSend={setFilesToSend}
            />
          </>
        )}
      </div>
    </>
  )
}

export default PatientsChat
